import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';

interface SatisfactionData {
  totalRatings: number;
  averageRating: number;
  highlyRatedContractors: number;
  satisfactionRate: number;
}

const CustomerSatisfactionDashboard: React.FC = () => {
  const [satisfactionData, setSatisfactionData] = useState<SatisfactionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { get } = useApi();

  useEffect(() => {
    const fetchSatisfactionData = async () => {
      try {
        setLoading(true);
        const response = await get('/api/admin/kpis/customer-satisfaction');
        if (response.success) {
          setSatisfactionData(response.data);
        } else {
          setError(response.message || 'Failed to fetch satisfaction data');
        }
      } catch (err: any) {
        setError(err.message || 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchSatisfactionData();
  }, [get]);

  if (loading) return <div className="p-6">Loading satisfaction data...</div>;
  if (error) return <div className="p-6 text-red-500">Error: {error}</div>;
  if (!satisfactionData) return <div className="p-6 text-gray-600">No satisfaction data available</div>;

  const getRatingColor = (rating: number) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 3.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSatisfactionRateColor = (rate: number) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <span key={i} className="text-yellow-400">★</span>
      );
    }

    if (hasHalfStar) {
      stars.push(
        <span key="half" className="text-yellow-400">☆</span>
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <span key={`empty-${i}`} className="text-gray-300">★</span>
      );
    }

    return stars;
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Customer Satisfaction Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Average Rating */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Average Rating</h3>
          <div className="flex items-center mb-2">
            <div className={`text-3xl font-bold mr-2 ${getRatingColor(satisfactionData.averageRating)}`}>
              {satisfactionData.averageRating.toFixed(1)}
            </div>
            <div className="text-2xl">
              {renderStars(satisfactionData.averageRating)}
            </div>
          </div>
          <p className="text-sm text-gray-600">Across all contractors</p>
        </div>

        {/* Total Ratings */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Ratings</h3>
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {satisfactionData.totalRatings}
          </div>
          <p className="text-sm text-gray-600">Customer reviews received</p>
        </div>

        {/* Highly Rated Contractors */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Highly Rated Contractors</h3>
          <div className="text-3xl font-bold text-green-600 mb-2">
            {satisfactionData.highlyRatedContractors}
          </div>
          <p className="text-sm text-gray-600">4.5+ star contractors</p>
        </div>

        {/* Satisfaction Rate */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Satisfaction Rate</h3>
          <div className={`text-3xl font-bold mb-2 ${getSatisfactionRateColor(satisfactionData.satisfactionRate)}`}>
            {satisfactionData.satisfactionRate.toFixed(1)}%
          </div>
          <p className="text-sm text-gray-600">Highly rated contractors</p>
        </div>
      </div>

      {/* Rating Distribution */}
      <div className="mt-8 bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Rating Distribution</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Rating Categories</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Excellent (4.5-5.0):</span>
                <span className="font-semibold text-green-600">
                  {satisfactionData.highlyRatedContractors} contractors
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Good (3.5-4.4):</span>
                <span className="font-semibold text-yellow-600">
                  {Math.max(0, Math.floor(satisfactionData.totalRatings * 0.3))} contractors
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Needs Improvement (0-3.4):</span>
                <span className="font-semibold text-red-600">
                  {Math.max(0, Math.floor(satisfactionData.totalRatings * 0.1))} contractors
                </span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Performance Insights</h4>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                {satisfactionData.averageRating >= 4.5
                  ? 'Outstanding customer satisfaction! Your contractors are delivering excellent service.'
                  : satisfactionData.averageRating >= 3.5
                  ? 'Good customer satisfaction. Focus on maintaining quality and addressing any issues.'
                  : 'Customer satisfaction needs improvement. Review contractor performance and customer feedback.'
                }
              </p>
              <p className="text-sm text-gray-600">
                {satisfactionData.satisfactionRate >= 80
                  ? 'High percentage of highly rated contractors indicates strong overall performance.'
                  : satisfactionData.satisfactionRate >= 60
                  ? 'Moderate percentage of highly rated contractors. Consider additional training or support.'
                  : 'Low percentage of highly rated contractors. Immediate attention needed to improve quality.'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Recommendations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-blue-800 mb-2">For High Performance</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Continue current quality standards</li>
              <li>• Share best practices across contractors</li>
              <li>• Consider expanding successful contractors</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-blue-800 mb-2">For Improvement</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Provide additional training programs</li>
              <li>• Implement quality assurance processes</li>
              <li>• Review customer feedback regularly</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSatisfactionDashboard;

